import React from 'react';
import { SignInButton, SignUpButton } from '@clerk/react';
import ScanForm from './ScanForm';
import ClaimFeedback from './ClaimFeedback';
import LazyClaimLanding from './LazyClaimLanding';
import { AuthState } from './AuthState';
import { useCreditInfo } from '../hooks/useCreditInfo';
import { useIsMobile } from '../hooks/useMediaQuery';
import { ScanSectionOptions } from '../utils/api';
import { FULL_AUDIT_CHECK_COUNT } from '@privisy/ui-bridge';
import { T } from '../designTokens';

// ---------------------------------------------------------------------------
// Hero section
// ---------------------------------------------------------------------------

interface HeroSectionProps {
  authEnabled: boolean;
  isLoading: boolean;
  handleScan: (urls: string[], options: { sections: ScanSectionOptions; headless: boolean }) => void;
  creditInfo: ReturnType<typeof useCreditInfo>;
  claimParams: { scanId: string | null; intentToken: string | null; domain: string | null; score: number | null; grade: string | null };
  isClaiming: boolean;
  claimResult: { type: 'success' | 'error'; message: string; claimedScanId?: string } | null;
  setClaimResult: (result: { type: 'success' | 'error'; message: string; claimedScanId?: string } | null) => void;
  clearClaimParams: () => void;
  showPaymentBanner: boolean;
  scanRouteId: string | null;
  /** Signed in, or auth disabled (dev mode). */
  isSignedIn?: boolean;
  /** "Audit again" on a past site loads its URL into the form. */
  prefill?: { url: string; nonce: number } | null;
}

export default function HeroSection({
  authEnabled,
  isLoading,
  handleScan,
  creditInfo,
  claimParams,
  isClaiming,
  claimResult,
  setClaimResult,
  clearClaimParams,
  showPaymentBanner,
  scanRouteId,
  isSignedIn = true,
  prefill,
}: HeroSectionProps) {
  const isMobile = useIsMobile();
  const isPostPayment = showPaymentBanner && !!scanRouteId;
  const isClaimLanding = !!claimParams.scanId && authEnabled;
  const scanForm = (
    <ScanForm onSubmit={handleScan} isLoading={isLoading} creditInfo={creditInfo} prefill={prefill} />
  );

  // The workspace: a signed-in customer (or dev mode) lands on one task,
  // auditing a site. They came here from the marketing site or a past
  // session, so the band names the task and what it spends rather than
  // re-introducing the product.
  const workspace = (
    <div style={{ maxWidth: '880px', margin: '0 auto', textAlign: 'left' }}>
      <h1
        style={{
          margin: '0 0 6px',
          fontSize: isMobile ? '24px' : '26px',
          fontWeight: 600,
          fontFamily: T.fontDisplay,
          letterSpacing: '-0.01em',
          color: T.gray100,
        }}
      >
        Audit a site
      </h1>
      <p style={{ margin: '0 0 24px', fontSize: '15px', lineHeight: 1.6, color: T.gray400, maxWidth: '62ch' }}>
        A full audit runs all {FULL_AUDIT_CHECK_COUNT} checks against the live site and opens the report here.
      </p>
      <ClaimFeedback
        isClaiming={isClaiming}
        claimResult={claimResult}
        onClose={() => setClaimResult(null)}
      />
      {scanForm}
    </div>
  );

  return (
    <section
      style={{
        backgroundColor: T.gray900,
        width: '100%',
        padding: isMobile ? '32px 16px 36px' : '44px 24px 48px',
        color: T.white,
        // The claim landing and the signed-out post-payment screen have no
        // body under them, so they absorb the leftover viewport height; the
        // workspace band stays compact above the customer's sites.
        flex: isClaimLanding || (authEnabled && !isSignedIn) ? '1 0 auto' : '0 0 auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
      className="scanner-home__band"
    >
      <div style={{ maxWidth: isClaimLanding ? '720px' : '880px', width: '100%', margin: '0 auto', textAlign: 'center' }}>
        {isClaimLanding && claimParams.scanId ? (
          /* Teaser document, full-bleed: it brings its own hero/body zones.
             No masked result on this lane — the claim identity here has no
             active #/scan/:id route to poll (e.g. cookie-recovered claim). */
          <div style={{ textAlign: 'left', borderRadius: '12px', overflow: 'hidden' }}>
            <LazyClaimLanding
              scanId={claimParams.scanId}
              isClaiming={isClaiming}
              claimResult={claimResult}
              onDismiss={() => { setClaimResult(null); clearClaimParams(); }}
              isMobile={isMobile}
              authEnabled={authEnabled}
              domain={claimParams.domain}
              score={claimParams.score}
              grade={claimParams.grade}
              maskedResult={null}
            />
          </div>
        ) : (
          authEnabled ? (
            <AuthState
              signedIn={workspace}
              signedOut={(
                <div style={{ textAlign: 'center' }}>
                  {isPostPayment ? (
                    <>
                      <p style={{ margin: '0 0 8px', color: T.gray200, fontSize: '16px', fontWeight: 600 }}>
                        Your report is ready. Sign in to access it.
                      </p>
                      <p style={{ margin: '0 0 20px', color: T.gray400, fontSize: '14px' }}>
                        Your payment was successful. Create an account or sign in to view your report and access it anytime.
                      </p>
                    </>
                  ) : (
                    // Signed-out visitors on the dashboard's own pages
                    // are sent to the marketing sign-in (App.tsx), so
                    // this only shows on a route that kept them here.
                    <p style={{ margin: '0 0 20px', color: T.gray200, fontSize: '16px', fontWeight: 600 }}>
                      Sign in to see your audits.
                    </p>
                  )}
                  {isPostPayment ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                      <SignUpButton mode="modal" forceRedirectUrl="/?welcome=1">
                        <button
                          style={{
                            padding: '12px 32px',
                            fontSize: '15px',
                            fontWeight: 600,
                            color: T.white,
                            backgroundColor: T.cobalt600,
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'background-color 0.15s',
                            minHeight: '44px',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = T.cobalt700; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = T.cobalt600; }}
                        >
                          Create Account to View Report
                        </button>
                      </SignUpButton>
                      <SignInButton mode="modal">
                        <button
                          style={{
                            padding: '8px 24px',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: T.gray400,
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          Already have an account? Sign in
                        </button>
                      </SignInButton>
                    </div>
                  ) : (
                    <SignInButton mode="modal">
                      <button
                        style={{
                          minHeight: '44px',
                          padding: '0 28px',
                          fontSize: '15px',
                          fontWeight: 600,
                          color: T.white,
                          backgroundColor: T.cobalt600,
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                        }}
                      >
                        Sign in
                      </button>
                    </SignInButton>
                  )}
                </div>
              )}
            />
          ) : (
            workspace
          )
        )}
      </div>
    </section>
  );
}
